import React, { useState } from "react";
import { Heart } from "lucide-react";
import { api, localDate } from "@/lib/api";

const MOODS = [
  { key: "loved", emoji: "🥰", label: "Loved" },
  { key: "happy", emoji: "😊", label: "Happy" },
  { key: "missing", emoji: "🥺", label: "Missing you" },
  { key: "excited", emoji: "🤩", label: "Excited" },
  { key: "sleepy", emoji: "😴", label: "Sleepy" },
  { key: "silly", emoji: "🤪", label: "Silly" },
  { key: "grumpy", emoji: "😤", label: "Grumpy" },
  { key: "blushing", emoji: "😳", label: "Blushing" },
  { key: "cozy", emoji: "🤗", label: "Cozy" },
  { key: "flirty", emoji: "😏", label: "Flirty" },
  { key: "stressed", emoji: "😩", label: "Stressed" },
  { key: "grateful", emoji: "🥹", label: "Grateful" },
  { key: "moody", emoji: "😒", label: "Moody" },
];

export default function FunZone({ config, setConfig, moods, setMoods, names }) {
  const [bursting, setBursting] = useState([]);
  const [who, setWho] = useState(names[0]);

  const sendHug = async () => {
    const hearts = Array.from({ length: 14 }, (_, i) => ({
      id: Date.now() + i,
      left: 10 + Math.random() * 80,
      delay: Math.random() * 0.5,
      size: 18 + Math.random() * 26,
      rot: (Math.random() * 60 - 30) + "deg",
    }));
    setBursting((b) => [...b, ...hearts]);
    setTimeout(() => {
      setBursting((b) => b.filter((h) => !hearts.find((x) => x.id === h.id)));
    }, 3000);
    const { data } = await api.post(`/hug?client_date=${localDate()}`);
    setConfig((c) => ({ ...c, hug_count: data.hug_count }));
  };

  const checkIn = async (m) => {
    const { data } = await api.post("/moods", { who, mood: m.emoji, label: m.label });
    setMoods((x) => [data, ...x].slice(0, 12));
  };

  return (
    <section id="fun" className="bg-[#F4F1EA] border-t border-[#EADEDA]">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="mb-8">
          <p className="font-hand text-2xl text-[#D95F59]">just for giggles</p>
          <h2 className="font-display text-4xl tracking-tight">Fun Zone</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Virtual hug */}
          <div className="card-paper rounded-3xl p-8 text-center flex flex-col items-center justify-center">
            <p className="text-[#756D6A] mb-1">Today you've shared</p>
            <div data-testid="hug-count" className="font-display text-6xl text-[#D95F59]">
              {config.hug_count ?? 0}
            </div>
            <p className="text-[#756D6A] mb-1">virtual hugs 🤗</p>
            <p className="text-xs text-[#8C9A86] mb-6">resets fresh every day 🌅</p>
            <button
              data-testid="send-hug-button"
              onClick={sendHug}
              className="flex items-center gap-2 rounded-full bg-[#D95F59] text-white px-8 py-4 text-lg hover:bg-[#C75D53] hover:scale-105 active:scale-95 transition-all shadow-soft"
            >
              <Heart className="w-5 h-5 fill-white" /> Send a Virtual Hug
            </button>
          </div>

          {/* Mood check-in */}
          <div className="card-paper rounded-3xl p-8">
            <h3 className="font-display text-2xl mb-1">How are you feeling?</h3>
            <div className="flex items-center gap-2 mb-4 text-sm">
              {names.map((n) => (
                <button
                  key={n}
                  data-testid={`mood-who-${n}`}
                  onClick={() => setWho(n)}
                  className={`px-3 py-1 rounded-full transition-colors ${
                    who === n ? "bg-[#D95F59] text-white" : "bg-[#FFF5F5] text-[#756D6A]"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3 mb-5">
              {MOODS.map((m) => (
                <button
                  key={m.key}
                  data-testid={`mood-${m.key}`}
                  onClick={() => checkIn(m)}
                  className="rounded-2xl bg-[#FFF5F5] border border-[#EADEDA] py-3 hover:-translate-y-1 hover:border-[#D95F59] transition-all"
                >
                  <div className="text-3xl">{m.emoji}</div>
                  <div className="text-xs text-[#756D6A] mt-1">{m.label}</div>
                </button>
              ))}
            </div>
            {moods.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="mood-history">
                {moods.slice(0, 6).map((m) => (
                  <span key={m.id} className="text-sm bg-[#F4F1EA] rounded-full px-3 py-1">
                    {m.mood} {m.who}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {bursting.map((h) => (
        <Heart
          key={h.id}
          className="heart-float text-[#D95F59] fill-[#D95F59]"
          style={{
            left: `${h.left}%`,
            width: h.size,
            height: h.size,
            animationDelay: `${h.delay}s`,
            "--r": h.rot,
          }}
        />
      ))}
    </section>
  );
}
